import React from "react"
import Chart from "react-apexcharts"
import {FutureCalc, IFutureYear, IExpenseCategorySummary, IExpenseItemSummary} from "../../../../components/calculator/Calculator"
import {ExpenseCategory} from "../../../../model/Expense"
import {SPEND_TO_THE_END_ID_PREFIX} from "../../../../components/calculator/SpendToTheEndSolver"
import {numberToMoneyFormat} from "../../../../stores/StoreUtilities"
import {useTheme} from "@mui/material"

const SpendingBreakdownChart = ({
  futureCalc,
}: {
  futureCalc: FutureCalc
  inflationRate?: number  // kept for backward-compat; no longer used
}) => {
  const theme = useTheme()

  const years: number[] = []
  const essential: { x: number; y: number }[] = []
  const discretionary: { x: number; y: number }[] = []
  const extra: { x: number; y: number }[] = []
  let hasExtra = false

  // Display in nominal (future) dollars to match the Budget view and the rest
  // of the app. The summary text below the chart still expresses the target
  // residual in today's dollars since that's how the user enters it.
  // Sum every Spend-to-the-End phase Expense's contribution for the year — Smile
  // injects three locked Expenses (Early/Mid/Late), Linear/Front-loaded one.
  // All share the "spend-to-the-end-" prefix so the chart can total them generically.
  futureCalc.futureYears.forEach((fy: IFutureYear) => {
    years.push(fy.year)
    const spendToTheEndItems = fy.budgetCalc?.expenseSummary?.byCategory
      ?.find((c: IExpenseCategorySummary) => c.category === ExpenseCategory.GivingAndMisc)
      ?.byItem.filter((i: IExpenseItemSummary) => i.id.startsWith(SPEND_TO_THE_END_ID_PREFIX)) ?? []
    const ext = spendToTheEndItems.reduce((sum, item) => sum + item.total, 0)
    const disc = (fy.discretionaryExpenses ?? 0) - ext
    essential.push({ x: fy.year, y: Math.round(fy.essentialExpenses ?? 0) })
    discretionary.push({ x: fy.year, y: Math.round(Math.max(0, disc)) })
    extra.push({ x: fy.year, y: Math.round(ext) })
    if (ext > 0) hasExtra = true
  })

  const series: any[] = [
    { name: "Essential", data: essential },
    { name: "Discretionary", data: discretionary },
  ]
  if (hasExtra) {
    series.push({ name: "Spend to the End Extra", data: extra })
  }

  const options: any = {
    chart: {
      id: "spending-breakdown",
      type: "bar",
      stacked: true,
      height: 320,
      width: "100%",
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    plotOptions: {
      bar: { columnWidth: "80%" },
    },
    colors: [
      theme.palette.grey[500],
      theme.palette.primary.light,
      theme.palette.secondary.main,
    ],
    dataLabels: { enabled: false },
    legend: {
      position: "top",
      horizontalAlign: "left",
      showForSingleSeries: true,
    },
    xaxis: {
      type: "numeric",
      tickAmount: Math.min(years.length, 10),
      labels: { formatter: (val: any) => `${Math.round(Number(val))}` },
      title: { text: "Year" },
    },
    yaxis: {
      labels: { formatter: (val: number) => numberToMoneyFormat(val, 0) },
      title: { text: "Spending (future $)" },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (val: number) => numberToMoneyFormat(val, 0) },
    },
  }

  return <Chart options={options} series={series} type="bar" height={320}/>
}

export default SpendingBreakdownChart
